import { computed, Injectable, signal } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { UserModel, UserTypes } from 'src/app/types/api/dot-net-api-types/data-contracts';

@Injectable({
  providedIn: 'root'
})
export class UserDataService {
  private sessionUser$ = new ReplaySubject<UserModel | null>(1);

  public get sessionUser(): Observable<UserModel | null> {
    return this.sessionUser$.asObservable();
  }

  public set sessionUser(user: UserModel | null) {
    this._currentUser.set(user);
    this.sessionUser$.next(user);
  }

  public completeSessionUser() {
    this.sessionUser$.complete();
  }

  // -------------------------------------------------------------------------------------------------------------------
  // Signal Based Architecture
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * The current user logged in.
   * @private
   */
  private _currentUser = signal<UserModel | null>(null);

  /**
   * Flag that tracks if user is loading.
   * @private
   */
  private _isLoading = signal<boolean>(true);

  /**
   * Flag that tracks if user initialization has started or not.
   * @private
   */
  private _hasInitialized = signal<boolean>(false);

  /**
   * Read-only signal for the currently logged-in user.
   */
  currentUser = this._currentUser.asReadonly();

  /**
   * Read-only signal that tracks if the user is still loading.
   */
  isLoading = this._isLoading.asReadonly();

  /**
   * Read-only signal that tracks if user initialization has started.
   */
  hasInitialized = this._hasInitialized.asReadonly();

  /**
   * Computed read-only signal that is true when the user is authenticated.
   */
  isAuthenticated = computed<boolean>(() => this._currentUser() !== null);

  /**
   * Computed read-only signal that holds the current logged-in user's userType.
   */
  userType = computed<UserTypes | null>(() => this.currentUser()?.userTypeCode ?? null);

  /**
   * Computed signal that tracks if loading and initialization has finished.
   */
  hasFinishedLoading = computed(() => !this._isLoading() && this._hasInitialized());

  // -------------------------------------------------------------------------------------------------------------------
  // Computed Role Signals
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Computed signal — true if user is Admin.
   */
  readonly isAdmin = computed<boolean>(() => this.userType() === UserTypes.Admin);

  /**
   * Computed signal — true if user is a System Administrator.
   *
   * System Administrators manage agency-profile configuration (document types and,
   * in future, case types, party types, etc.) via the SystemAdminController.
   */
  readonly isSystemAdmin = computed<boolean>(() => this.userType() === UserTypes.SystemAdmin);

  /**
   * Computed signal — true if user is an Attorney.
   */
  readonly isAttorney = computed<boolean>(() => this.userType() === UserTypes.Attorney);

  /**
   * Computed signal — true if user is a Judge.
   */
  readonly isJudge = computed<boolean>(() => this.userType() === UserTypes.Judge);

  /**
   * Computed signal — true if user is self-represented (Self Represented Litigant).
   */
  readonly isSelfRepresented = computed<boolean>(() => this.userType() === UserTypes.SelfRep);

  /**
   * Computed signal — true if user is a court user (Admin or Judge).
   */
  readonly isCourtUser = computed<boolean>(() =>
    this.isAdmin() || this.isJudge()
  );

  /**
   * Computed signal — true if user is external (Attorney or SelfRepresentedLitigant).
   */
  readonly isExternalUser = computed<boolean>(() =>
    this.isAttorney() || this.isSelfRepresented()
  );

  /**
   * Computed signal — true if user is an internal court-side user:
   * Admin, SystemAdmin, CourtStaff, ClerkStaff, JudicialStaff, or Judge.
   */
  readonly isInternalUser = computed<boolean>(() => {
    const type = this.userType();
    return type === UserTypes.Admin ||
      type === UserTypes.SystemAdmin ||
      type === UserTypes.CourtStaff ||
      type === UserTypes.ClerkStaff ||
      type === UserTypes.JudicialStaff ||
      type === UserTypes.Judge;
  });

  // -------------------------------------------------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Called when user data has started loading.
   * @param loading
   */
  setLoading(loading: boolean): void {
    this._isLoading.set(loading);
  }

  /**
   * Called when auth initialization is complete.
   */
  setInitialized(): void {
    this._hasInitialized.set(true);
    this._isLoading.set(false);
  }

  /**
   * A single role check.
   * @param role
   */
  hasRole(role: UserTypes): boolean {
    return this.userType() === role;
  }

  /**
   * Multiple role check.
   * @param roles
   */
  hasAnyRole(roles: UserTypes[]): boolean {
    const currUserType = this.userType();
    return currUserType !== null && roles.includes(currUserType);
  }
}
